import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Copy, ExternalLink, Eye, MessageSquare, Sparkles, Wrench } from "lucide-react";
import SeverityChip from "./SeverityChip";
import AIFixPanel from "./AIFixPanel";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function FindingDrawer({ finding, open, onOpenChange, onUpdated }) {
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState(finding?.status || "open");
  const [revealed, setRevealed] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(finding?.status || "open");
    setRevealed(null);
    setComment("");
  }, [finding]);

  if (!finding) return null;
  const f = revealed || finding;
  const ev = f.evidence || {};
  const rem = f.remediation || {};

  const copy = (text) => {
    navigator.clipboard.writeText(text || "");
    toast.success("Copied to clipboard.");
  };

  const reveal = async () => {
    const r = await api.post(`/findings/${finding.id}/reveal-evidence`);
    setRevealed(r.data);
    toast.success("Sensitive headers revealed (audit-logged).");
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { status };
      if (comment.trim()) body.comment = comment.trim();
      if (status === "false_positive") body.false_positive_reason = comment || "Marked false positive";
      await api.patch(`/findings/${finding.id}`, body);
      toast.success("Triage updated.");
      onUpdated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl bg-paper overflow-y-auto" data-testid="finding-drawer">
        <SheetHeader>
          <div className="flex items-center gap-2 mb-2">
            <SeverityChip severity={f.severity} />
            {f.cvss_score != null && (
              <span className="text-xs text-ink/60 font-mono">CVSS {f.cvss_score}</span>
            )}
            {f.cwe_id && (
              <a target="_blank" rel="noreferrer"
                 href={`https://cwe.mitre.org/data/definitions/${(f.cwe_id || "").split("-")[1] || ""}.html`}
                 className="text-xs underline text-ink/70 inline-flex items-center gap-1" data-testid="drawer-cwe-link">
                {f.cwe_id} <ExternalLink size={10} />
              </a>
            )}
            {f.owasp_category && (() => {
              // f.owasp_category looks like "A02:2025 — Security Misconfiguration"
              const m = (f.owasp_category || "").match(/^(A\d{2}):(\d{4})\s*—\s*(.+)$/);
              if (!m) return <span className="text-xs text-ink/60">{f.owasp_category}</span>;
              const [, code, year, name] = m;
              const slug = name.replace(/[^A-Za-z0-9]+/g, "_");
              const href = `https://owasp.org/Top10/${year}/${code}_${year}-${slug}/`;
              return (
                <a target="_blank" rel="noreferrer" href={href}
                   className="text-xs underline text-ink/70 inline-flex items-center gap-1"
                   data-testid="drawer-owasp-link">
                  {f.owasp_category} <ExternalLink size={10} />
                </a>
              );
            })()}
          </div>
          <SheetTitle className="font-display text-2xl text-left" data-testid="drawer-title">{f.title}</SheetTitle>
          <SheetDescription className="text-left text-ink/60">{f.asset}</SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="evidence" className="mt-6">
          <TabsList className="grid grid-cols-4 w-full" data-testid="drawer-tabs">
            <TabsTrigger value="evidence" data-testid="tab-evidence">Evidence</TabsTrigger>
            <TabsTrigger value="fix" data-testid="tab-fix">Fix It</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai">
              <Sparkles size={12} className="mr-1 text-gold" /> AI
            </TabsTrigger>
            <TabsTrigger value="triage" data-testid="tab-triage">Triage</TabsTrigger>
          </TabsList>

          <TabsContent value="evidence" className="space-y-3">
            {ev.type === "code" && (
              <>
                <div className="flex items-center justify-between text-xs text-ink/60">
                  <span>{ev.file}:{ev.line}</span>
                  <button onClick={() => copy(ev.snippet)} className="inline-flex items-center gap-1 hover:text-ink" data-testid="copy-snippet">
                    <Copy size={12} /> Copy
                  </button>
                </div>
                <pre className="bg-panel text-ink p-4 rounded-lg text-xs overflow-x-auto font-mono whitespace-pre" data-testid="evidence-snippet">{ev.snippet}</pre>
              </>
            )}
            {ev.type === "http" && (
              <>
                <div className="flex items-center justify-between text-xs text-ink/60">
                  <span>Request / Response</span>
                  <div className="flex gap-3">
                    {!revealed && (
                      <button onClick={reveal} className="inline-flex items-center gap-1 hover:text-ink" data-testid="reveal-evidence">
                        <Eye size={12} /> Reveal sensitive
                      </button>
                    )}
                    <button onClick={() => copy(`${ev.request}\n\n${ev.response}`)} className="inline-flex items-center gap-1 hover:text-ink" data-testid="copy-evidence">
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-ink/50 mb-1">Request</h4>
                  <pre className="bg-panel text-ink p-3 rounded-lg text-xs overflow-x-auto font-mono whitespace-pre">{ev.request}</pre>
                </div>
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-ink/50 mb-1">Response</h4>
                  <pre className="bg-panel text-ink p-3 rounded-lg text-xs overflow-x-auto font-mono whitespace-pre">{ev.response}</pre>
                </div>
              </>
            )}
            {ev.type === "dependency" && (
              <div className="space-y-2 text-sm">
                <div><span className="text-ink/60">Dependency:</span> <span className="font-mono">{ev.dependency_path}</span></div>
                <div><span className="text-ink/60">CVE:</span> <span className="font-mono">{ev.cve_record}</span></div>
                <div><span className="text-ink/60">Vulnerable range:</span> <span className="font-mono">{(ev.vulnerable_range || []).join(", ")}</span></div>
                <p className="text-ink/80 pt-2 border-t">{ev.summary}</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="fix" className="space-y-4">
            <div className="flex items-start gap-3">
              <Wrench size={18} className="text-safe mt-0.5" />
              <div>
                <h3 className="font-semibold mb-1">Fix It.</h3>
                <p className="text-sm text-ink/80">{rem.summary}</p>
              </div>
            </div>
            {(rem.steps || []).length > 0 && (
              <ol className="list-decimal pl-6 space-y-1.5 text-sm text-ink/80" data-testid="fix-steps">
                {rem.steps.map((s, i) => <li key={`step-${i}-${s.slice(0, 24)}`}>{s}</li>)}
              </ol>
            )}
            {(rem.before_code || rem.after_code) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                {rem.before_code && (
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-sev-critical mb-1">Before</h4>
                    <pre className="bg-panel text-ink p-3 rounded-lg font-mono whitespace-pre-wrap">{rem.before_code}</pre>
                  </div>
                )}
                {rem.after_code && (
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-safe mb-1">After</h4>
                    <pre className="bg-panel text-ink p-3 rounded-lg font-mono whitespace-pre-wrap">{rem.after_code}</pre>
                  </div>
                )}
              </div>
            )}
            {(rem.references || []).length > 0 && (
              <div className="pt-3 border-t">
                <h4 className="text-xs uppercase tracking-wider text-ink/50 mb-2">References</h4>
                <ul className="space-y-1">
                  {rem.references.map((r, i) => (
                    <li key={r.url || `ref-${i}`}>
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-sm text-gold hover:underline inline-flex items-center gap-1">
                        {r.label} <ExternalLink size={12} />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TabsContent>

          <TabsContent value="ai" className="space-y-4" data-testid="tab-ai-content">
            <AIFixPanel finding={f} onApplied={() => { onUpdated?.(); onOpenChange(false); }} />
          </TabsContent>

          <TabsContent value="triage" className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-ink/50">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-12" data-testid="triage-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="triaged">Triaged</SelectItem>
                  <SelectItem value="fixed">Fixed</SelectItem>
                  <SelectItem value="accepted">Accepted Risk</SelectItem>
                  <SelectItem value="false_positive">False Positive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink/50">Comment / reason</label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={status === "false_positive" ? "Why is this a false positive?" : "Add a comment for your team…"}
                className="min-h-[100px]"
                data-testid="triage-comment"
              />
            </div>
            <Button onClick={save} disabled={saving} className="bg-gold text-paper hover:bg-gold2 h-12 w-full" data-testid="triage-save">
              <MessageSquare size={16} className="mr-2" /> {saving ? "Saving…" : "Update finding"}
            </Button>

            <div className="pt-4 border-t">
              <h4 className="text-xs uppercase tracking-wider text-ink/50 mb-2">Timeline</h4>
              <ul className="space-y-2 text-sm">
                <li className="text-ink/70">First seen: <span className="font-mono">{new Date(f.first_seen).toLocaleString()}</span></li>
                <li className="text-ink/70">Last seen: <span className="font-mono">{new Date(f.last_seen).toLocaleString()}</span></li>
                {(f.history || []).map((h, i) => (
                  <li key={`hist-${h.at}-${i}`} className="text-ink/70">
                    <span className="font-mono text-xs">{new Date(h.at).toLocaleString()}</span> · {h.change}
                  </li>
                ))}
              </ul>
              {(f.comments || []).length > 0 && (
                <>
                  <h4 className="text-xs uppercase tracking-wider text-ink/50 mb-2 mt-4">Comments</h4>
                  <ul className="space-y-2">
                    {f.comments.map((c, i) => (
                      <li key={`cmt-${c.at}-${i}`} className="text-sm bg-raised rounded-md p-2">
                        <div className="text-xs text-ink/50">{c.by_name || c.by} · {new Date(c.at).toLocaleString()}</div>
                        <div>{c.text}</div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
